import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import emailjs from "emailjs-com";
import React, { useState } from "react";
import popUpImg from "../Images/popup.png";

interface ModalContentProps {
  onClose: () => void;
  selectedService: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
}

export const InnerModalContent: React.FC<ModalContentProps> = ({
  onClose,
  selectedService,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    serviceType: selectedService || "Residence",
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const handleServiceChange = (event: SelectChangeEvent<string>) => {
    setFormData((previous) => ({ ...previous, serviceType: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    setSending(true);
    setMessage("");

    const templateParams = {
      from_name: formData.name,
      email: formData.email,
      phone: formData.phone,
      service_type: formData.serviceType,
      message: "Message not available",
    };

    try {
      await emailjs.send(
        "service_ez66qzn",
        "template_a4hk5m4",
        templateParams,
        "R9Yl9lyT79sQieGAV",
      );
      setMessage("Message sent successfully!");
      setTimeout(() => {
        onClose();
        setFormData({
          name: "",
          email: "",
          phone: "",
          serviceType: "Residence",
        });
      }, 900);
    } catch (error) {
      setMessage("Failed to send message. Please try again.");
    }

    setSending(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      {!isMobile ? (
        <Box
          component="img"
          src={popUpImg}
          alt="Vista Fusion Studio interior consultation"
          sx={{ width: "100%", display: "block", borderRadius: "18px" }}
          onContextMenu={(event) => event.preventDefault()}
        />
      ) : null}

      <Box
        sx={{
          position: isMobile ? "static" : "absolute",
          top: isMobile ? "auto" : "50%",
          right: isMobile ? "auto" : "3%",
          transform: isMobile ? "none" : "translateY(-50%)",
          width: { xs: "100%", sm: "58%", md: "40%" },
          borderRadius: "16px",
          p: { xs: 1.8, md: 2.1 },
          background:
            "linear-gradient(152deg, rgba(255,255,255,0.96), rgba(255,247,243,0.84))",
          border: "1px solid rgba(128,51,65,0.18)",
          boxShadow: "0 16px 28px rgba(20,8,10,0.16)",
        }}
      >
        <IconButton
          sx={{ position: "absolute", top: 8, right: 8 }}
          onClick={onClose}
        >
          <CloseRoundedIcon />
        </IconButton>

        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1.85rem",
            fontWeight: 700,
            color: "#33171d",
            lineHeight: 1.08,
            textAlign: "center",
          }}
        >
          Get in Touch
        </Typography>
        <Typography
          sx={{
            mt: 0.6,
            textAlign: "center",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.85rem",
            color: "rgba(51,23,29,0.78)",
          }}
        >
          Share your details and we’ll reach out shortly.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1.55 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleTextChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 1.35 }}
            size="small"
            disabled={sending}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleTextChange}
            error={!!errors.email}
            helperText={errors.email}
            sx={{ mb: 1.35 }}
            size="small"
            disabled={sending}
          />

          <TextField
            fullWidth
            label="Phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleTextChange}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={{ mb: 1.35 }}
            size="small"
            disabled={sending}
          />

          <FormControl fullWidth sx={{ mb: 1.6 }} size="small">
            <Select
              name="serviceType"
              value={formData.serviceType}
              onChange={handleServiceChange}
              disabled={sending}
            >
              <MenuItem value="Residence">Residence</MenuItem>
              <MenuItem value="Commercial">Commercial</MenuItem>
              <MenuItem value="Retail">Retail</MenuItem>
              <MenuItem value="Hospitality">Hospitality</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            fullWidth
            type="submit"
            disabled={sending}
            endIcon={<ArrowOutwardRoundedIcon />}
            sx={{
              py: 1,
              borderRadius: "999px",
              textTransform: "none",
              fontFamily: '"Manrope", sans-serif',
              fontWeight: 700,
              background: "linear-gradient(137deg, #803341 0%, #a85156 100%)",
              "&:hover": {
                background: "linear-gradient(137deg, #6f2c39 0%, #954349 100%)",
              },
            }}
          >
            {sending ? "Sending..." : "Send Inquiry"}
          </Button>

          {message ? (
            <Typography
              sx={{
                textAlign: "center",
                color: message.includes("Failed") ? "#b64141" : "#3f8a4f",
                mt: 1.35,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.84rem",
              }}
            >
              {message}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
